import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  signal,
  untracked,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type {
  OnyxDropdownOptionsSource,
  OnyxIconName,
  OnyxSuggestion,
} from '@onyx/angular';
import {
  DropdownHelper,
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxFormMode,
  OnyxIconComponent,
  OnyxMessageComponent,
  OnyxNamePipe,
  OnyxSectionComponent,
  OnyxSuggestionsComponent,
} from '@onyx/angular';
import { chain, intersection, isEqual } from 'lodash';
import {
  catchError,
  combineLatestWith,
  distinctUntilChanged,
  map,
  of,
  ReplaySubject,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { CommonHelper } from '../../../../common/helpers/common.helper';
import {
  ValidationConditionsHelper,
  ValidationContext,
} from '../../../../common/helpers/validation-conditions.helper';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import { FleetIdentifierPipe } from '../../../../common/pipes/fleet-identifier.pipe';
import { JoinPipe } from '../../../../common/pipes/join.pipe';
import { DriverValidationHelper } from '../../../drivers/common/helpers/driver-validation.helper';
import { DriverHelper } from '../../../drivers/common/helpers/driver.helper';
import type { Driver } from '../../../drivers/common/interfaces/driver';
import { DriverCategory } from '../../../drivers/common/interfaces/driver-category';
import { DriversService } from '../../../drivers/common/services/drivers.service';
import { TRAILER_CATEGORIES } from '../../../fleet/common/constants/trailer-categories';
import { VEHICLE_CATEGORIES } from '../../../fleet/common/constants/vehicle-categories';
import { FleetCategory } from '../../../fleet/common/enums/fleet-category';
import { FleetState } from '../../../fleet/common/enums/fleet-state';
import { FleetValidationHelper } from '../../../fleet/common/helpers/fleet-validation.helper';
import { FleetHelper } from '../../../fleet/common/helpers/fleet.helper';
import type { Fleet } from '../../../fleet/common/interfaces/fleet';
import { FleetService } from '../../../fleet/common/services/fleet.service';
import { getFleetSuggestions } from '../../common/constants/fleet-suggestions';
import { getSecondaryDriverSuggestions } from '../../common/constants/secondary-driver-suggestions';
import { DelegationsFormService } from '../../common/services/delegations-form.service';

type DelegationDriverAndVehicleFormGroup = ReturnType<
  typeof DelegationDriverAndVehicleFormComponent.buildForm
>;

interface PlanningCountriesMessage {
  intent: ReturnType<OnyxMessageComponent['intent']>;
  icon: OnyxIconName;
  message: string;
  countries: string;
}

@Component({
  selector: 'app-delegation-driver-and-vehicle-form',
  imports: [
    OnyxSectionComponent,
    OnyxDropdownComponent,
    ReactiveFormsModule,
    OnyxIconComponent,
    OnyxFormGroupComponent,
    TranslatePipe,
    OnyxMessageComponent,
    OnyxButtonComponent,
    OnyxSuggestionsComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './delegation-driver-and-vehicle-form.component.html',
  styleUrl: './delegation-driver-and-vehicle-form.component.scss',
})
export class DelegationDriverAndVehicleFormComponent implements OnInit {
  private readonly driversService = inject(DriversService);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly fleetHelper = inject(FleetHelper);
  private readonly fleetService = inject(FleetService);
  private readonly joinPipe = inject(JoinPipe);
  private readonly namePipe = inject(OnyxNamePipe);
  private readonly delegationsFormService = inject(DelegationsFormService);
  private readonly driverHelper = inject(DriverHelper);
  private readonly fleetIdentifierPipe = inject(FleetIdentifierPipe);
  private readonly fleetValidationHelper = inject(FleetValidationHelper);
  private readonly driverValidationHelper = inject(DriverValidationHelper);
  private readonly validationConditionsHelper = inject(
    ValidationConditionsHelper,
  );

  public readonly form = input.required<DelegationDriverAndVehicleFormGroup>();
  public readonly mode = input.required<OnyxFormMode>();
  public readonly isActive = input.required<boolean>();
  public readonly isFinished = input.required<boolean>();

  protected readonly I18N = 'delegations.driverAndVehicleForm';
  protected readonly VEHICLES_SOURCE: OnyxDropdownOptionsSource<Fleet> = {
    list: (query, pagination) =>
      this.refresh$.pipe(startWith(undefined)).pipe(
        switchMap(() =>
          this.fleetService.searchFleet(query, {
            ...pagination,
            filters: [
              FleetHelper.createCategoryFilter(
                this.trailer()
                  ? this.trailer()!.generalInformation.category ===
                    FleetCategory.SEMI_TRAILER
                    ? [FleetCategory.SEMI_TRUCK]
                    : [FleetCategory.STRAIGHT_TRUCK, FleetCategory.VAN]
                  : [...VEHICLE_CATEGORIES],
              ),
              FleetHelper.createStateFilter(FleetState.ACTIVE),
            ],
          }),
        ),
        map((result) => ({
          ...result,
          options: chain(DropdownHelper.mapToOptions(result.options)!)
            .map((option) => {
              const trailer = this.trailer();
              const driver = this.primaryDriver() ?? undefined;
              const secondaryDriver = this.secondaryDriver() ?? undefined;
              const validationBody = {
                context: ValidationContext.DELEGATION,
                primary: option.value,
              };

              if (trailer) {
                return this.fleetValidationHelper.validateSets({
                  ...validationBody,
                  options: {
                    secondary: trailer,
                    driver,
                    secondaryDriver,
                    option,
                  },
                }).option!;
              }

              return this.fleetValidationHelper.validateFleet({
                ...validationBody,
                options: {
                  option,
                  driver,
                  secondaryDriver,
                },
              }).option!;
            })
            .thru((options) =>
              this.validationConditionsHelper.groupOptions(options),
            )
            .value(),
        })),
      ),
    get: (uuid) =>
      this.fleetService.getFleet(uuid).pipe(
        map((vehicle) => ({
          name: this.fleetIdentifierPipe.transform(vehicle),
          value: vehicle,
        })),
      ),
    idKey: 'uuid',
  };

  protected readonly TRAILERS_SOURCE: OnyxDropdownOptionsSource<Fleet> = {
    list: (query, pagination) =>
      this.refresh$.pipe(startWith(undefined)).pipe(
        switchMap(() =>
          this.fleetService.searchFleet(query, {
            ...pagination,
            filters: [
              FleetHelper.createCategoryFilter(
                this.vehicle()
                  ? this.vehicle()!.generalInformation.category ===
                    FleetCategory.SEMI_TRUCK
                    ? [FleetCategory.SEMI_TRAILER]
                    : [FleetCategory.TRAILER]
                  : [...TRAILER_CATEGORIES],
              ),
              FleetHelper.createStateFilter(FleetState.ACTIVE),
            ],
          }),
        ),
        map((result) => ({
          ...result,
          options: chain(DropdownHelper.mapToOptions(result.options)!)
            .map((option) => {
              const vehicle = this.vehicle();
              const driver = this.primaryDriver() ?? undefined;
              const secondaryDriver = this.secondaryDriver() ?? undefined;

              if (vehicle) {
                return this.fleetValidationHelper.validateSets({
                  context: ValidationContext.DELEGATION,
                  primary: vehicle,
                  options: {
                    warningsFocus: 'trailer',
                    secondary: option.value,
                    driver,
                    secondaryDriver,
                    option,
                  },
                }).option!;
              }
              return this.fleetValidationHelper.validateFleet({
                context: ValidationContext.DELEGATION,
                primary: option.value,
                options: {
                  option,
                  driver,
                  secondaryDriver,
                },
              }).option!;
            })
            .thru((options) =>
              this.validationConditionsHelper.groupOptions(options),
            )
            .value(),
        })),
      ),
    get: (uuid) =>
      this.fleetService.getFleet(uuid).pipe(
        map((trailer) => ({
          name: this.fleetIdentifierPipe.transform(trailer),
          value: trailer,
        })),
      ),
    idKey: 'uuid',
  };

  protected readonly DRIVERS_SOURCE: OnyxDropdownOptionsSource<Driver> = {
    list: (query, pagination) =>
      this.driversService
        .searchDrivers(query, {
          ...pagination,
          filters: [DriverHelper.createCategoryFilter(DriverCategory.ACTIVE)],
        })
        .pipe(
          combineLatestWith(this.refresh$.pipe(startWith(undefined))),
          map(([result]) => ({
            ...result,
            options: chain(DropdownHelper.mapToOptions(result.options))
              .filter(({ value }) =>
                chain(this.form().getRawValue())
                  .pick(['primaryDriverUuid', 'secondaryDriverUuid'])
                  .values()
                  .compact()
                  .thru((drivers) => !drivers.includes(value.uuid))
                  .value(),
              )
              .map((option) => {
                return this.driverValidationHelper.validate({
                  context: ValidationContext.DELEGATION,
                  driver: option.value,
                  options: {
                    option,
                    vehicle: this.vehicle() ?? undefined,
                    trailer: this.trailer() ?? undefined,
                  },
                }).option!;
              })
              .thru((options) =>
                this.validationConditionsHelper.groupOptions(options),
              )
              .value(),
          })),
        ),

    get: (uuid) =>
      this.driversService.getDriver(uuid).pipe(
        map((driver) => ({
          name: `${driver.driverData.firstName} ${driver.driverData.lastName}`,
          value: driver,
        })),
      ),
    idKey: 'uuid',
  };

  protected readonly OnyxFormMode = OnyxFormMode;

  protected primaryDriver = signal<Driver | null>(null);
  protected secondaryDriver = signal<Driver | null>(null);
  protected vehicle = signal<Fleet | null>(null);
  protected trailer = signal<Fleet | null>(null);
  protected secondaryDriverSuggestions = signal<
    OnyxSuggestion<string>[] | null
  >(null);
  protected vehicleSuggestions = signal<OnyxSuggestion<string>[] | null>(null);
  protected trailerSuggestions = signal<OnyxSuggestion<string>[] | null>(null);

  protected trailerLabel = computed(() =>
    FleetHelper.getTrailerLabel(this.vehicle()),
  );

  protected links = computed(() => ({
    primaryDriver: CommonHelper.getPreviewLink(this.primaryDriver(), (driver) =>
      this.driverHelper.openModal(driver),
    ),
    secondaryDriver: CommonHelper.getPreviewLink(
      this.secondaryDriver(),
      (driver) => this.driverHelper.openModal(driver),
    ),
    vehicle: CommonHelper.getPreviewLink(this.vehicle(), (vehicle) =>
      this.fleetHelper.openModal(vehicle),
    ),
    trailer: CommonHelper.getPreviewLink(this.trailer(), (trailer) =>
      this.fleetHelper.openModal(trailer),
    ),
  }));

  protected planningCountriesMessage =
    computed<PlanningCountriesMessage | null>(() => {
      const [vehicle, primaryDriver] = [this.vehicle(), this.primaryDriver()];
      if (!vehicle || !primaryDriver) return null;

      const trailer = this.trailer();
      const fleetCountries = trailer
        ? intersection(
            vehicle.allowedCountries.whitelist,
            trailer.allowedCountries.whitelist,
          )
        : vehicle.allowedCountries.whitelist;

      const secondaryDriver = this.secondaryDriver();
      const driversCountries = secondaryDriver
        ? intersection(
            primaryDriver.countries.whitelist,
            secondaryDriver.countries.whitelist,
          )
        : primaryDriver.countries.whitelist;

      const delegationCountries = intersection(
        fleetCountries,
        driversCountries,
      );

      const isNeutral = isEqual(
        fleetCountries.toSorted(),
        driversCountries.toSorted(),
      );

      return {
        intent: isNeutral ? 'neutral' : 'warning',
        icon: isNeutral ? 'tooltip-filled' : 'alert-circle-two-tone-filled',
        message: isNeutral ? 'planningCountries' : 'planningCountriesLimited',
        countries: this.joinPipe.transform(
          CommonHelper.getGroupedCountriesKeys(delegationCountries),
        ),
      };
    });
  private refresh$ = new ReplaySubject<void>();

  public ngOnInit(): void {
    effect(
      () => {
        this.delegationsFormService.crew.set({
          primaryDriver: this.primaryDriver(),
          secondaryDriver: this.secondaryDriver(),
          vehicle: this.vehicle(),
          trailer: this.trailer(),
        });

        untracked(() => this.refresh$.next());
      },
      { injector: this.injector },
    );

    const controls = this.form().controls;

    if (this.mode() === OnyxFormMode.EDIT) {
      ValidationHelper.disableControls(
        [controls.primaryDriverUuid, controls.secondaryDriverUuid],
        { reset: false },
      );
    }

    effect(
      () => {
        this.delegationsFormService.driverBase.set(
          this.primaryDriver()?.driverData.assignedBase?.address ?? null,
        );
        this.delegationsFormService.driverHome.set(
          this.primaryDriver()?.driverData.homeAddress ?? null,
        );
        this.delegationsFormService.vehicleBase.set(
          this.vehicle()?.generalInformation.assignedBase?.address ?? null,
        );
      },
      { injector: this.injector },
    );

    toObservable(this.primaryDriver, { injector: this.injector })
      .pipe(
        map((driver) => driver?.assignedVehicle?.uuid ?? null),
        distinctUntilChanged(),
        tap(() => this.resetSuggestions()),
        switchMap((uuid) =>
          uuid
            ? this.fleetService.getFleet(uuid).pipe(catchError(() => of(null)))
            : of(null),
        ),
      )
      .subscribe((fleet) => this.vehicle.set(fleet));

    effect(
      () => {
        if (this.isActive() || this.isFinished()) return;

        const vehicle = this.vehicle();
        if (!vehicle) {
          this.resetFleetSuggestions();
          return;
        }

        const {
          vehicleUuid,
          trailerUuid,
          primaryDriverUuid,
          secondaryDriverUuid,
        } = this.form().controls;

        if (primaryDriverUuid.value) {
          this.vehicleSuggestions.set(
            getFleetSuggestions(vehicle, this.fleetIdentifierPipe),
          );
        }
        if (!vehicleUuid.value) vehicleUuid.setValue(vehicle.uuid);

        const trailer = vehicle.trailer;
        if (trailer) {
          this.trailerSuggestions.set(
            getFleetSuggestions(trailer, this.fleetIdentifierPipe),
          );
          if (!trailerUuid.value) trailerUuid.setValue(trailer.uuid);
        }

        const secondaryDriverValue = vehicle.drivers?.secondaryDriver;
        const secondaryDriver =
          secondaryDriverValue?.uuid !== primaryDriverUuid.value
            ? secondaryDriverValue
            : vehicle.drivers?.primaryDriver;

        this.secondaryDriverSuggestions.set(
          secondaryDriver
            ? getSecondaryDriverSuggestions(secondaryDriver, this.namePipe)
            : null,
        );
        if (!secondaryDriverUuid.value) {
          secondaryDriverUuid.setValue(secondaryDriver?.uuid ?? null);
        }
      },
      { injector: this.injector },
    );

    if (this.isActive()) {
      ValidationHelper.disableControls(this.form(), { reset: false });
    }
  }

  protected planChange(vehicle: Fleet): void {
    CommonHelper.showOnMap(this.router, vehicle.uuid, this.injector, {
      showSwapPanel: true,
    });
  }

  private resetFleetSuggestions(): void {
    this.vehicleSuggestions.set(null);
    this.trailerSuggestions.set(null);
  }

  private resetSuggestions(): void {
    this.resetFleetSuggestions();
    this.secondaryDriverSuggestions.set(null);
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      primaryDriverUuid: fb.control<string | null>(null, [Validators.required]),
      secondaryDriverUuid: fb.control<string | null>(null),
      vehicleUuid: fb.control<string | null>(null, [Validators.required]),
      trailerUuid: fb.control<string | null>(null),
    });
  }
}
